import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { motion } from 'framer-motion'
import { 
  Download, 
  Upload, 
  Archive, 
  Trash2, 
  RefreshCw, 
  Clock, 
  HardDrive,
  Users,
  AlertCircle,
  CheckCircle,
  Plus,
  Search,
  X
} from 'lucide-react'
import toast from 'react-hot-toast'
import { format } from 'date-fns'
import { apiService } from '../../services/api'
import LoadingSpinner from '../common/LoadingSpinner'
import ConfirmationModal from '../common/ConfirmationModal'
import { Button } from '../ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { cn } from '../../utils/cn'

const ClientBackupManager = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [selectedClient, setSelectedClient] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showBulkBackupConfirm, setShowBulkBackupConfirm] = useState(false);
  const [backupToDelete, setBackupToDelete] = useState(null);
  const [selectedBackup, setSelectedBackup] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [isCreatingBackup, setIsCreatingBackup] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  
  // Fetch paginated backups
  const { data: backupData = { backups: [], total: 0 }, isLoading, isFetching, error, refetch } = useQuery(
    ['backups', currentPage, pageSize, searchTerm, filterStatus],
    async () => {
      const response = await apiService.get('/backups', {
        params: {
          page: currentPage,
          limit: pageSize,
          search: searchTerm,
          status: filterStatus !== 'all' ? filterStatus : undefined
        }
      });
      return response.data;
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 30000, // 30 seconds
      keepPreviousData: true
    }
  );
  
  const backups = backupData.backups || [];
  const totalBackups = backupData.total || 0;
  const totalPages = Math.ceil(totalBackups / pageSize);

  // Fetch clients for backup creation — use same key/fetcher as ClientManager so the cache is shared
  const { data: clientsData, isLoading: clientsLoading } = useQuery(
    ['clients'],
    () => apiService.get('/clients').then(res => res.data),
    { staleTime: 30000 }
  );
  const clients = clientsData?.clients || [];

  // Create backup mutation
  const createBackupMutation = useMutation(
    async (clientId) => {
      const response = await apiService.post(`/backups/client/${clientId}`, {
        type: 'manual'
      });
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Backup created successfully');
        queryClient.invalidateQueries('backups');
        setShowCreateModal(false);
        setSelectedClient('');
        setIsCreatingBackup(false);
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to create backup');
        setIsCreatingBackup(false);
      }
    }
  );

  // Delete backup mutation
  const deleteBackupMutation = useMutation(
    async (backupId) => {
      await apiService.delete(`/backups/${backupId}`);
    },
    {
      onSuccess: () => {
        toast.success('Backup deleted successfully');
        queryClient.invalidateQueries('backups');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to delete backup');
      }
    }
  );

  // Bulk backup mutation
  const bulkBackupMutation = useMutation(
    async (clientIds) => {
      const response = await apiService.post('/backups/bulk', {
        clientIds,
        type: 'manual'
      });
      return response.data;
    },
    {
      onSuccess: (data) => {
        toast.success(`Bulk backup completed: ${data.summary.successful}/${data.summary.total}`);
        queryClient.invalidateQueries('backups');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to create bulk backup');
      }
    }
  );

  // Server-side filtering, so we just use the backups directly
  const filteredBackups = backups;

  const handleCreateBackup = async () => {
    if (!selectedClient) {
      toast.error('Please select a client');
      return;
    }

    setIsCreatingBackup(true);
    createBackupMutation.mutate(selectedClient);
  };

  const handleDeleteBackup = (backup) => {
    setBackupToDelete(backup);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = () => {
    if (backupToDelete) {
      deleteBackupMutation.mutate(backupToDelete.id);
      setShowDeleteConfirm(false);
      setBackupToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setBackupToDelete(null);
  };

  const handleDownloadBackup = async (backup) => {
    try {
      // Use fetch to handle the download properly with authentication
      const response = await apiService.get(`/backups/${backup.id}/download`, {
        responseType: 'blob', // Important for binary data
        headers: {
          'Accept': 'application/zip'
        }
      });

      // Create blob URL and download
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      // Use sanitized filename (remove special chars)
      const sanitizedFilename = backup.fileName.replace(/[^a-zA-Z0-9.-]/g, '_');
      link.download = sanitizedFilename;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the blob URL
      window.URL.revokeObjectURL(url);
      
      toast.success('Backup downloaded successfully');
    } catch (error) {
      console.error('Download error:', error);
      const errorMsg = error.response?.data?.error || 'Failed to download backup';
      toast.error(errorMsg);
    }
  };

  const handleRestoreBackup = (backup) => {
    setSelectedBackup(backup);
    setShowRestoreModal(true);
  };

  const confirmRestore = async () => {
    try {
      await apiService.post(`/backups/${selectedBackup.id}/restore`);
      toast.success('Backup restoration initiated');
      setShowRestoreModal(false);
      setSelectedBackup(null);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to restore backup');
    }
  };

  const handleBulkBackup = () => {
    if (!Array.isArray(clients) || clients.length === 0) {
      toast.error('No clients available for backup');
      return;
    }
    
    const selectedClientIds = clients.map(client => client.id);
    if (selectedClientIds.length === 0) {
      toast.error('No clients available for backup');
      return;
    }

    setShowBulkBackupConfirm(true);
  };

  const handleBulkBackupConfirm = () => {
    const selectedClientIds = clients.map(client => client.id);
    bulkBackupMutation.mutate(selectedClientIds);
    setShowBulkBackupConfirm(false);
  };

  const handleBulkBackupCancel = () => {
    setShowBulkBackupConfirm(false);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-success-500" />
      case 'pending':
        return <Clock className="w-5 h-5 text-warning-500" />
      case 'failed':
        return <AlertCircle className="w-5 h-5 text-error-500" />
      default:
        return <Clock className="w-5 h-5 text-neutral-500" />
    }
  }

  if (isLoading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center">
        <div>
          <h1 className="text-3xl font-bold text-neutral-900 dark:text-neutral-100">
            Client Backup Manager
          </h1>
          <p className="text-neutral-600 dark:text-neutral-400">
            Create, manage, and restore client site backups
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={() => setShowCreateModal(true)}
            disabled={isCreatingBackup}
          >
            <Plus className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Create Backup</span>
          </Button>
          <Button
            onClick={handleBulkBackup}
            disabled={bulkBackupMutation.isLoading}
            variant="outline"
            className="border-neutral-200 dark:border-[#2f2b41] bg-white dark:bg-[#221f2f] text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-[#2f2b41] hover:text-neutral-900 dark:hover:text-neutral-100"
          >
            <Archive className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Bulk Backup</span>
          </Button>
          <Button
            onClick={() => refetch()}
            disabled={isFetching}
            variant="outline"
            size="sm"
            className="border-neutral-200 dark:border-[#2f2b41] bg-white dark:bg-[#221f2f] text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-[#2f2b41] hover:text-neutral-900 dark:hover:text-neutral-100"
          >
            <RefreshCw className={`w-4 h-4 sm:mr-2 ${isFetching ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">{isFetching ? 'Refreshing...' : 'Refresh'}</span>
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <Card className="hover:shadow-lg transition-all duration-300">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="space-y-2">
                  <p className="text-sm font-medium text-neutral-600 dark:text-neutral-400">Total Backups</p>
                  <p className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">{totalBackups}</p>
                </div>
                <div className="p-3 bg-accent-100 dark:bg-accent-900/20 rounded-xl">
                  <Archive className="w-6 h-6 text-accent-600 dark:text-accent-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Card className="hover:shadow-lg transition-all duration-300">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="space-y-2">
                  <p className="text-sm font-medium text-neutral-600 dark:text-neutral-400">Storage Used</p>
                  <p className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
                    {/* Debug storage calculation */}
                    {(() => {
                      const storageMB = backupData.totalStorageMB;
                      const storageBytes = backupData.totalStorageBytes;
                      
                      if (storageBytes && storageBytes > 0) {
                        return `${(storageBytes / (1024 * 1024)).toFixed(1)} MB`;
                      } else if (storageMB && storageMB > 0) {
                        return `${storageMB.toFixed(1)} MB`;
                      } else {
                        return '0.0 MB';
                      }
                    })()}
                  </p>
                </div>
                <div className="p-3 bg-success-100 dark:bg-success-900/20 rounded-xl">
                  <HardDrive className="w-6 h-6 text-success-600 dark:text-success-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <Card className="hover:shadow-lg transition-all duration-300">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="space-y-2">
                  <p className="text-sm font-medium text-neutral-600 dark:text-neutral-400">Clients with Backups</p>
                  <p className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
                    {backupData.uniqueClients || 0}
                  </p>
                </div>
                <div className="p-3 bg-primary-100 dark:bg-primary-900/20 rounded-xl">
                  <Users className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <Card className="hover:shadow-lg transition-all duration-300">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="space-y-2">
                  <p className="text-sm font-medium text-neutral-600 dark:text-neutral-400">Successful</p>
                  <p className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
                    {backupData.successfulBackups || 0}
                  </p>
                </div>
                <div className="p-3 bg-emerald-100 dark:bg-emerald-900/20 rounded-xl">
                  <CheckCircle className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.5 }}
      >
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search backups..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value)
                      setCurrentPage(1) // Reset to first page when searching
                    }}
                    className="w-full pl-10 pr-4 py-2 border border-neutral-300 dark:border-[#2f2b41] rounded-md bg-white dark:bg-[#221f2f] text-neutral-900 dark:text-neutral-100 placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent"
                  />
                </div>
              </div>
              <div className="w-40">
                <select
                  value={filterStatus}
                  onChange={(e) => {
                    setFilterStatus(e.target.value)
                    setCurrentPage(1) // Reset to first page when filtering
                  }}
                  className="w-full px-3 py-2 pr-8 border border-neutral-300 dark:border-[#2f2b41] rounded-md bg-white dark:bg-[#221f2f] text-neutral-900 dark:text-neutral-100 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent appearance-none cursor-pointer"
                >
                  <option value="all">All Status</option>
                  <option value="completed">Completed</option>
                  <option value="pending">Pending</option>
                  <option value="failed">Failed</option>
                </select>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Backups List */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.6 }}
      >
        <Card>
          {filteredBackups.length === 0 ? (
            <CardContent className="text-center py-12">
              <Archive className="mx-auto h-12 w-12 text-neutral-400" />
              <h3 className="mt-2 text-sm font-medium text-neutral-900 dark:text-neutral-100">No backups found</h3>
              <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
                Get started by creating your first backup.
              </p>
            </CardContent>
          ) : (
            <CardContent className="p-0">
              <ul className="divide-y divide-neutral-200 dark:divide-neutral-700">
                {filteredBackups.map((backup, index) => (
                  <li
                    key={backup.id}
                    className="hover:bg-neutral-50 dark:hover:bg-[#201d2e] transition-colors"
                  >
                    <div className="px-4 py-4 sm:px-6">
                      {/* Mobile: stacked layout */}
                      <div className="flex flex-col gap-2 sm:hidden">
                        <div className="flex items-start gap-3">
                          <div className="shrink-0 mt-0.5">{getStatusIcon(backup.status)}</div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100 truncate">
                              {backup.fileName}
                            </p>
                            <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                              {backup.clientName} · {backup.fileSize}
                            </p>
                            <p className="text-xs text-neutral-500 dark:text-neutral-400">
                              {format(new Date(backup.createdAt), 'MMM dd, yyyy HH:mm')}
                            </p>
                          </div>
                          <span className="shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-accent-100 text-accent-800 dark:bg-accent-900/20 dark:text-accent-200">
                            {backup.sitesCount} {backup.sitesCount === 1 ? 'site' : 'sites'}
                          </span>
                        </div>
                        <div className="flex items-center justify-end gap-1">
                          <div className="relative group/tip">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDownloadBackup(backup)}
                              className="rounded-xl text-accent-600 hover:text-accent-700 hover:bg-accent-100 dark:text-accent-400 dark:hover:text-accent-300 dark:hover:bg-accent-900/30 transition-all duration-200"
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-1 bg-neutral-900 dark:bg-neutral-800 text-white text-xs rounded-md opacity-0 group-hover/tip:opacity-100 transition-opacity pointer-events-none whitespace-nowrap shadow-md z-10">
                              Download
                            </div>
                          </div>
                          <div className="relative group/tip">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleRestoreBackup(backup)}
                              className="rounded-xl text-success-600 hover:text-success-700 hover:bg-success-100 dark:text-success-400 dark:hover:text-success-300 dark:hover:bg-success-900/30 transition-all duration-200"
                              disabled={backup.status !== 'completed'}
                            >
                              <Upload className="w-4 h-4" />
                            </Button>
                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-1 bg-neutral-900 dark:bg-neutral-800 text-white text-xs rounded-md opacity-0 group-hover/tip:opacity-100 transition-opacity pointer-events-none whitespace-nowrap shadow-md z-10">
                              Restore
                            </div>
                          </div>
                          <div className="relative group/tip">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteBackup(backup)}
                              className="rounded-xl text-error-600 hover:text-error-700 hover:bg-error-100 dark:text-error-400 dark:hover:text-error-300 dark:hover:bg-error-900/30 transition-all duration-200"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-1 bg-neutral-900 dark:bg-neutral-800 text-white text-xs rounded-md opacity-0 group-hover/tip:opacity-100 transition-opacity pointer-events-none whitespace-nowrap shadow-md z-10">
                              Delete
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Desktop: original horizontal layout */}
                      <div className="hidden sm:flex items-center justify-between">
                        <div className="flex items-center min-w-0">
                          {getStatusIcon(backup.status)}
                          <div className="ml-4 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100 truncate">
                                {backup.fileName}
                              </p>
                              <span className="shrink-0 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-accent-100 text-accent-800 dark:bg-accent-900/20 dark:text-accent-200">
                                {backup.sitesCount} {backup.sitesCount === 1 ? 'site' : 'sites'}
                              </span>
                            </div>
                            <div className="mt-1 flex items-center text-sm text-neutral-500 dark:text-neutral-400">
                              <span>{backup.clientName}</span>
                              <span className="mx-2">•</span>
                              <span>{backup.fileSize}</span>
                              <span className="mx-2">•</span>
                              <span>{format(new Date(backup.createdAt), 'MMM dd, yyyy HH:mm')}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-1 shrink-0 ml-4">
                          <div className="relative group/tip">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDownloadBackup(backup)}
                              className="rounded-xl text-accent-600 hover:text-accent-700 hover:bg-accent-100 dark:text-accent-400 dark:hover:text-accent-300 dark:hover:bg-accent-900/30 transition-all duration-200"
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-1 bg-neutral-900 dark:bg-neutral-800 text-white text-xs rounded-md opacity-0 group-hover/tip:opacity-100 transition-opacity pointer-events-none whitespace-nowrap shadow-md z-10">
                              Download
                            </div>
                          </div>
                          <div className="relative group/tip">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleRestoreBackup(backup)}
                              className="rounded-xl text-success-600 hover:text-success-700 hover:bg-success-100 dark:text-success-400 dark:hover:text-success-300 dark:hover:bg-success-900/30 transition-all duration-200"
                              disabled={backup.status !== 'completed'}
                            >
                              <Upload className="w-4 h-4" />
                            </Button>
                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-1 bg-neutral-900 dark:bg-neutral-800 text-white text-xs rounded-md opacity-0 group-hover/tip:opacity-100 transition-opacity pointer-events-none whitespace-nowrap shadow-md z-10">
                              Restore
                            </div>
                          </div>
                          <div className="relative group/tip">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteBackup(backup)}
                              className="rounded-xl text-error-600 hover:text-error-700 hover:bg-error-100 dark:text-error-400 dark:hover:text-error-300 dark:hover:bg-error-900/30 transition-all duration-200"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-1 bg-neutral-900 dark:bg-neutral-800 text-white text-xs rounded-md opacity-0 group-hover/tip:opacity-100 transition-opacity pointer-events-none whitespace-nowrap shadow-md z-10">
                              Delete
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </CardContent>
          )}
        </Card>
      </motion.div>

      {/* Pagination */}
      {totalPages > 1 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.7 }}
        >
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="text-sm text-neutral-600 dark:text-neutral-400">
                  Showing {Math.min((currentPage - 1) * pageSize + 1, totalBackups)} to {Math.min(currentPage * pageSize, totalBackups)} of {totalBackups} backups
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="border-neutral-200 dark:border-[#2f2b41] bg-white dark:bg-[#221f2f] text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-[#2f2b41]"
                  >
                    Previous
                  </Button>
                  {/* Page numbers: hidden on mobile, visible on sm+ */}
                  <div className="hidden sm:flex items-center space-x-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      return (
                        <Button
                          key={pageNum}
                          variant={currentPage === pageNum ? "default" : "outline"}
                          size="sm"
                          onClick={() => setCurrentPage(pageNum)}
                          className={currentPage === pageNum
                            ? "bg-accent-600 text-white"
                            : "border-neutral-200 dark:border-[#2f2b41] bg-white dark:bg-[#221f2f] text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-[#2f2b41]"
                          }
                        >
                          {pageNum}
                        </Button>
                      );
                    })}
                  </div>
                  {/* Mobile: compact page indicator */}
                  <span className="sm:hidden text-sm text-neutral-600 dark:text-neutral-400">
                    {currentPage} / {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="border-neutral-200 dark:border-[#2f2b41] bg-white dark:bg-[#221f2f] text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-[#2f2b41]"
                  >
                    Next
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Create Backup Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-black/30 dark:bg-black/60 backdrop-blur-sm transition-all" onClick={() => setShowCreateModal(false)} />
            
            <div className="inline-block align-bottom bg-white dark:bg-[#1a1625] rounded-xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full border-0">
              <div className="px-6 pt-6 pb-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-neutral-900 dark:text-neutral-50">
                    Create New Backup
                  </h3>
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 p-1 rounded-lg hover:bg-neutral-100 dark:hover:bg-[#201d2e] transition-colors"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>

              <div className="px-6 pb-4">
                <div className="space-y-4">
                  <div className="bg-accent-50 dark:bg-accent-900/20 border border-accent-200 dark:border-accent-700 rounded-md p-3">
                    <div className="flex">
                      <Plus className="h-5 w-5 text-accent-600 dark:text-accent-400 mt-0.5" />
                      <div className="ml-3">
                        <p className="text-sm text-accent-800 dark:text-accent-200">
                          Create a new backup for a specific client's website and data.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      Select Client
                    </label>
                    <select
                      value={selectedClient}
                      onChange={(e) => setSelectedClient(e.target.value)}
                      className="h-9 w-full px-3 border border-neutral-200 dark:border-[#2f2b41] rounded-md bg-white dark:bg-[#221f2f] text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent"
                      disabled={clientsLoading}
                    >
                      <option value="">
                        {clientsLoading ? 'Loading clients...' : 'Choose a client...'}
                      </option>
                      {Array.isArray(clients) && clients.map((client) => (
                        <option key={client.id} value={client.id}>
                          {client.company_name} ({client.email || 'No email'})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              
              <div className="bg-neutral-50/50 dark:bg-[#1f1c2c] border-t border-neutral-200 dark:border-[#2f2b41] px-6 py-4 flex justify-end space-x-3">
                <Button
                  variant="outline"
                  onClick={() => setShowCreateModal(false)}
                  disabled={isCreatingBackup}
                  className="border-neutral-200 dark:border-[#2f2b41] bg-white dark:bg-[#221f2f] text-neutral-700 dark:text-neutral-300"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCreateBackup}
                  disabled={!selectedClient || isCreatingBackup}
                >
                  {isCreatingBackup ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4 mr-2" />
                      Create Backup
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Restore Backup Modal */}
      {showRestoreModal && selectedBackup && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-black/30 dark:bg-black/60 backdrop-blur-sm transition-all" onClick={() => setShowRestoreModal(false)} />
            
            <div className="inline-block align-bottom bg-white dark:bg-[#1a1625] rounded-xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full border-0">
              <div className="px-6 pt-6 pb-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-neutral-900 dark:text-neutral-50">
                    Restore Backup
                  </h3>
                  <button
                    onClick={() => setShowRestoreModal(false)}
                    className="text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 p-1 rounded-lg hover:bg-neutral-100 dark:hover:bg-[#201d2e] transition-colors"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>

              <div className="px-6 pb-4">
                <div className="space-y-4">
                  <div className="bg-warning-50 dark:bg-warning-900/20 border border-warning-200 dark:border-warning-700 rounded-md p-3">
                    <div className="flex">
                      <AlertCircle className="h-5 w-5 text-warning-600 dark:text-warning-400 mt-0.5" />
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-warning-800 dark:text-warning-200">
                          Warning
                        </h3>
                        <p className="mt-1 text-sm text-warning-700 dark:text-warning-300">
                          Restoring this backup will overwrite existing site data. This action cannot be undone.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-medium text-neutral-900 dark:text-neutral-100 mb-2">Backup Details:</h4>
                    <div className="bg-neutral-50 dark:bg-[#201d2e] rounded-md p-3 text-sm">
                      <div className="space-y-1 text-neutral-700 dark:text-neutral-300">
                        <div><strong className="text-neutral-900 dark:text-neutral-100">File:</strong> {selectedBackup.fileName}</div>
                        <div><strong className="text-neutral-900 dark:text-neutral-100">Client:</strong> {selectedBackup.clientName}</div>
                        <div><strong className="text-neutral-900 dark:text-neutral-100">Sites:</strong> {selectedBackup.sitesCount}</div>
                        <div><strong className="text-neutral-900 dark:text-neutral-100">Size:</strong> {selectedBackup.fileSize}</div>
                        <div><strong className="text-neutral-900 dark:text-neutral-100">Created:</strong> {format(new Date(selectedBackup.createdAt), 'MMM dd, yyyy HH:mm')}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-neutral-50/50 dark:bg-[#1f1c2c] border-t border-neutral-200 dark:border-[#2f2b41] px-6 py-4 flex justify-end space-x-3">
                <Button
                  variant="outline"
                  onClick={() => setShowRestoreModal(false)}
                  className="border-neutral-200 dark:border-[#2f2b41] bg-white dark:bg-[#221f2f] text-neutral-700 dark:text-neutral-300"
                >
                  Cancel
                </Button>
                <Button
                  onClick={confirmRestore}
                  variant="destructive"
                >
                  <AlertCircle className="h-4 w-4 mr-2" />
                  Restore Backup
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        isLoading={deleteBackupMutation.isLoading}
        title="Delete Backup"
        message={
          <div>
            Are you sure you want to delete backup{' '}
            <span className="font-medium text-neutral-900 dark:text-neutral-100">
              {backupToDelete?.fileName}
            </span>
            ? This action cannot be undone and will permanently remove the backup file.
          </div>
        }
        confirmText="Delete Backup"
        confirmVariant="destructive"
        icon={Trash2}
      />

      {/* Bulk Backup Confirmation Modal */}
      <ConfirmationModal
        isOpen={showBulkBackupConfirm}
        onClose={handleBulkBackupCancel}
        onConfirm={handleBulkBackupConfirm}
        isLoading={bulkBackupMutation.isLoading}
        title="Create Bulk Backup"
        message={
          <div>
            Are you sure you want to create backups for all{' '}
            <span className="font-medium text-neutral-900 dark:text-neutral-100">
              {clients?.length || 0} clients
            </span>
            ? This may take some time to complete.
          </div>
        }
        confirmText="Create Backups"
        confirmVariant="default"
        icon={Archive}
        iconColor="text-accent-600 dark:text-accent-400"
        iconBgColor="bg-accent-100 dark:bg-accent-900/20"
      />
    </div>
  );
};

export default ClientBackupManager;